<template>
    <div class="planner">

        <div class="planner__periods-wrapper">
            <div class="planner__period" v-for="(period, periodIndex) in periods" :data-period-index="periodIndex" v-if="period.start" :style="styleForPeriod(period)" @click.prevent="clickedPeriod($event, period)" :class="{ 'is-selected': periodIndex === selectedPeriod, 'is-enabled': period.enabled, 'is-disabled': !period.enabled, 'is-finalized': isFinalized(period) }">

                <div class="planner__period-header" :class="{ 'is-disabled' : !period.enabled}" :title="displayPeriodTitle(period)">
                    <span class="planner__period-title">{{ period.title }}</span>
                </div>

                <a href="#" class="planner__enabler" :class="{ 'is-disabled' : !period.enabled}" v-show="canEnable(period)" @click.prevent="togglePeriodEnabled(period)">{{ period.enabled ? 'disable' : 'enable' }}</a>

                <div class="planner__block" v-for="(block, blockIndex) in period.blocks" :class=" { 'is-disabled': !period.enabled, 'is-finalized': isBlockFinalized(period, block) }" :data-period-index="periodIndex" :data-block-index="blockIndex" :style="styleForBlock(block)" :title="displayBlockTitle(period, block)">
                    <div class="planner__extend-handle"> + - </div>
                </div>
            </div>
        </div>

        <div class="planner__calendar">

            <div class="planner__header">
                <div class="planner__week-relative-number">&nbsp;</div>
                <div v-for="day in displayedDaysInWeek" class="planner__day planner__day--heading">{{ day }}</div>
            </div>

            <div v-for="wk in displayedWeeks" class="planner__week" :class="wk.cls">
                <div class="planner__week-relative-number">{{ wk.relativeNumber }}</div>
                <div class="planner__week-days-wrapper" @click.prevent="clickedWeek($event, wk)" :title="wk.label">
                    <div v-for="day in wk.days" class="planner__day" :class="day"></div>
                </div>
                <div class="planner__week-number">{{ wk.number }}</div>
                <div class="planner__week-date">{{ wk.dates }}</div>
            </div>

        </div>

    </div>
</template>


<script>
import moment from 'js/helpers/moment'
import _cloneDeep from 'lodash/cloneDeep'
import interact from 'interactjs'

export default {
    name: 'WeekPlanning',
    mixins: [],
    components: {},

    props: {

        value: {
            type: Array,
        },

        minWeeksInPast: {
            type: Number,
            default: 3,
        },

        minWeeksInFuture: {
            type: Number,
            default: 52
        },

        maxWeeksInFuture: {
            type: Number,
            default: 52
        },

        allowEditInPast: {
            type: Boolean,
            default: false,
        },

        allowEditInCurrentWeek: {
            type: Boolean,
            default: false,
        },

        allowPeriodOverlap: {
            type: Boolean,
            default: false,
        },

        minBlockDuration: {
            type: Number,
            default: 1,
        },

        maxBlockDuration: {
            type: Number,
            default: 52,
        },

        allowMovingSelectedPeriods: {
            type: Boolean,
            default: true,
        },

        resetSelected: {
            type: Boolean,
            default: false,
        },

        relativeToPeriods: {
            type: Boolean,
            default: true,
        },

        classForWeek: {
            type: Function,
        },

        labelForWeek: {
            type: Function,
        }

    },

    data: function() {
        return {
            periods: _cloneDeep(this.value),
            selectedPeriod: null,

            displayedWeeks: [],
        }
    },

    watch: {
        value: {
            handler: function(newVal) {
                this.periods = _cloneDeep(newVal)
                this.autoResolveOverlap()
                if ( this.selectedPeriod != null && this.scheduledPeriods.length - 1 < this.selectedPeriod ) {
                    this.selectedPeriod = null
                }
            },
            deep: true
        },
        weeksToDisplay: function() {
            this.recomputeWeeks()
        },
    },

    computed: {

        // makes sure first period is visible
        start: function() {
            const regularStart = this.dateForCurrentWeek().subtract(this.minWeeksInPast, 'weeks')
            return this.firstPeriod != null && this.firstPeriod.start.isBefore( regularStart ) ? this.roundToWeek(this.firstPeriod.start) : regularStart
        },

        // makes sure last period is visible + padding to certain max
        end: function() {
            const lastScheduledWeek = this.lastPeriod != null ? this.lastPeriod.start.clone().add( this.periodDuration(this.lastPeriod), 'weeks' ) : this.dateForCurrentWeek()
            const firstScheduledWeek = this.firstPeriod != null && (this.firstPeriod.start.isBefore( this.dateForCurrentWeek() ) || this.relativeToPeriods) ?
                    this.roundToWeek(this.firstPeriod.start) :
                    this.dateForCurrentWeek().clone().add(this.allowEditInCurrentWeek ? 0 : 1, 'weeks')

            // minimally shown
            const a = moment.max( firstScheduledWeek.clone().add(this.minWeeksInFuture, 'weeks'), lastScheduledWeek )

            // try to pad to maxWeeks
            const b = moment.min( lastScheduledWeek.clone().add(10, 'weeks'), firstScheduledWeek.clone().add(this.maxWeeksInFuture, 'weeks') )

            return moment.max(a, b)

        },

        weeksInPast: function() {
            return this.dateForCurrentWeek().diff( this.start, 'weeks')
        },

        daysInWeek: function() {
            return 7
        },

        weeksToDisplay: function() {
            return this.end.diff( this.start, 'weeks')
        },

        scheduledPeriods: function() {
            return this.periods.filter( p => p.start != null )
        },

        firstPeriod: function() {
            return this.scheduledPeriods[0]
        },

        firstPeriodDiff: function() {
            if ( this.firstPeriod == null ) {
                return 0
            }

            return this.firstPeriod.start.diff( this.start, 'weeks' )
        },

        lastPeriod: function() {
            return this.scheduledPeriods[ this.scheduledPeriods.length - 1 ]
        },

        finalizedDate: function() {
            return this.allowEditInCurrentWeek ? this.dateForCurrentWeek() : moment()
        },


        displayedDaysInWeek: function() {
            let arr = []
            for (var i = 0; i < this.daysInWeek; i++) {
                arr.push(this.displayDay(i+1))
            }
            return arr
        },

    },

    methods: {

        recomputeWeeks: function() {
            // expensive method, caches all weeks data
            let arr = []
            for (let i = 0; i < this.weeksToDisplay; i++) {

                let createNew = (idx) => {
                    let days = []
                    for (let j = 0; j < this.daysInWeek; j++) {
                        days.push( this.classDay(j+1, idx) )
                    }

                    return {
                        index: idx,
                        cls: this.classWeek(idx),
                        number: this.displayWeekNumber(idx),
                        dates: this.displayWeekDates(idx),
                        label: this.displayWeekLabel(idx),
                        days: days,
                    }
                }

                let week = this.displayedWeeks[i] || createNew(i+1)
                week.relativeNumber = this.displayRelativeWeekNumber(i+1)

                arr[i] = week
            }

            this.displayedWeeks = arr
        },

        /** Display **/

        /**
         * @param  {Int} day 1-based day of week
         */
        displayDay: function(day) {
            return this.dateForDay(day).format('dd')
        },

        /**
         * @param  {Int} week 1-based week from start
         */
        displayWeekNumber: function(week) {
            const date = this.dateForWeek(week)
            return 'wk ' + date.format('w')
        },

        displayWeekDates: function(week) {
            const date = this.dateForWeek(week)
            return date.format('D MMM') + ' - ' + date.add(6, 'days').format('D MMM')
        },

        displayWeekLabel: function(week) {
            return this.labelForWeek == null ? '' : this.labelForWeek( this.dateForWeek(week) )
        },

        displayRelativeWeekNumber: function(week) {

            const format = number => number > 0 ? number : '-'

            if ( this.relativeToPeriods && this.firstPeriod != null ) {

                const diff = week - this.firstPeriodDiff
                return format(diff)

            }

            const weekFromNow = week - this.weeksInPast - (this.allowEditInCurrentWeek ? 0 : 1)
            return format(weekFromNow)
        },

        displayPeriodTitle: function(period) {
            return 'Periode ' + period.title + '. Versleep om te verplaatsen naar een andere week.'
        },

        displayBlockTitle: function(period, block) {
            return period.title + ', blok ' + block.title + '. Versleep om te verlengen/verkorten.'
        },

        /**
         * @param  {Int} day  1-based day of week
         * @param  {Int} week 1-based week from start
         * @return {Array}      array of css class strings
         */
        classDay: function(day, week) {

            let classes = [ this.dateForDay(day).locale('en').format('dddd').toLowerCase() ]

            if ( week <= this.weeksInPast ) {
                classes.push('past')
            } else if ( week === this.weeksInPast + 1 ) {

                const currentDay = moment().weekday()
                if (currentDay > day-1) {
                    classes.push('past')
                } else if ( currentDay === day-1) {
                    classes.push('current')
                }

            } else {
                classes.push('future')
            }

            // prefix BEM element
            return classes.map( c => 'planner__day--' + c)
        },

        classWeek: function(week) {
            return this.classForWeek == null ? '' : this.classForWeek( this.dateForWeek(week) )
        },

        /** Helpers **/

        /**
         * @param  {Int} day 1-based day of week
         */
        dateForDay: function(day) {
            // monday always first day of the week
            return moment().isoWeekday(day).startOf('day')
        },

        dateForCurrentWeek: function() {
            return this.roundToWeek( moment() )
        },

        roundToWeek: function(date) {
            return date.clone().startOf('week')
        },

        /**
         * @returns {Int} 1-based week since start
         */
        weekForDate: function(date) {
            return date.diff( this.start, 'weeks') + 1
        },

        dateForWeek: function(week) {
            return this.start.clone().add(week - 1, 'weeks')
        },

        periodDuration: function(period) {
            if ( !period.enabled ) {
                return 0
            }
            return period.blocks.reduce( (sum, b) => sum + b.duration, 0)
        },

        isFinalized: function(period) {
            return period.start.isBefore( this.finalizedDate )
        },

        isBlockFinalized: function(period, block) {
            const endDate = this.blockEndDate(period, block)
            return endDate.isBefore( this.finalizedDate )
        },

        blockEndDate: function(period, block) {
            const blockIndex = period.blocks.indexOf(block)
            const blocksDuration = period.blocks.slice(0, blockIndex+1).reduce( (sum, el) => sum + el.duration, 0)
            return period.start.clone().add(blocksDuration, 'weeks')
        },

        /**
         * @param  {Int} day 1-based week since start
         */
        canMoveToWeek: function(period, week) {
            if ( this.allowEditInPast ) {
                return true
            }

            const finalizedWeek = this.weeksInPast + (this.allowEditInCurrentWeek ? 0 : 1)

            return period.start.isSameOrAfter( this.finalizedDate ) && week > finalizedWeek
        },

        canExtend: function(period, block, weeks) {
            if ( this.allowEditInPast ) {
                return true
            }

            const blockEndDate = this.blockEndDate(period, block)

            if ( blockEndDate.isSameOrBefore( this.finalizedDate ) ) {
                return false
            }

            const newBlockDate = blockEndDate.clone().add(weeks, 'weeks')
            if ( newBlockDate.isSameOrBefore( this.finalizedDate ) ) {
                return false
            }

            return true

        },

        canEnable: function(period) {
            return this.canMoveToWeek(period, this.weekForDate(period.start))
        },


        /** Events **/

        clickedWeek: function(e, week) {

            if ( this.allowMovingSelectedPeriods && this.selectedPeriod != null ) {

                this.moveToWeek( this.periods[this.selectedPeriod], week )

                if ( this.resetSelected ) {
                    this.selectedPeriod = null
                }

            }

            const date = this.start.clone().add(week - 1, 'weeks')
            this.$emit('clickedDate', date)

        },

        clickedPeriod: function(e, period) {
            const clickedPeriod = this.periods.indexOf(period)
            if ( this.allowMovingSelectedPeriods ) {
                this.selectedPeriod = (clickedPeriod === this.selectedPeriod && this.resetSelected) ? null : clickedPeriod
            }
            this.$emit('clickedPeriod', this.selectedPeriod)

        },

        onPeriodMove: function(event) {
            const period = this.periods[event.target.dataset.periodIndex]
            const movement = Math.round(event.dy / 45)
            if ( movement !== 0 ) {
                this.moveWeeks( period, movement )
            }
        },

        onBlockMove: function(event) {
            const period = this.periods[event.target.dataset.periodIndex]
            const block = period.blocks[event.target.dataset.blockIndex]
            const movement = Math.round(event.dy / 45)
            if ( movement !== 0 ) {
                this.extend( period, block, movement )
            }
        },


        /** Period manipulation **/

        moveToWeek: function(period, week) {

            if ( !this.canMoveToWeek(period, week) ) {
                return
            }

            const newStart = this.start.clone().add(week - 1, 'weeks')
            period.start = newStart
            this.updated()

        },

        moveWeeks: function(period, weeks) {
            const newStart = period.start.clone().add(weeks, 'weeks')

            if ( !this.canMoveToWeek(period, this.weekForDate(newStart)) ) {
                return
            }

            period.start = newStart
            this.updated()
        },

        extend: function(period, block, weeks) {
            if ( !this.canExtend(period, block, weeks) ) {
                return
            }

            block.duration = Math.max( this.minBlockDuration, Math.min( block.duration + weeks, this.maxBlockDuration ))
            this.updated()
        },

        togglePeriodEnabled: function(period) {
            if ( !this.canEnable(period) ) {
                return
            }
            period.enabled = !period.enabled
            this.updated()
        },

        updated: function() {
            this.autoResolveOverlap()
            this.$emit('input', _cloneDeep(this.periods))
        },


        autoResolveOverlap: function() {
            this.resolveOverlap()
            this.resolveMaxWeeks()
        },

        resolveOverlap: function() {

            if ( !this.allowPeriodOverlap && this.periods.length > 1 ) {

                let previousPeriod = null
                for (const thisPeriod of this.periods) {

                    if ( previousPeriod != null && previousPeriod.start != null && thisPeriod.start != null ) {

                        const previousEndDate = previousPeriod.start.clone().add( this.periodDuration(previousPeriod), 'weeks' )

                        if ( thisPeriod.start.isBefore( previousEndDate ) ) {
                            thisPeriod.start = previousEndDate
                        }

                    }

                    previousPeriod = thisPeriod


                }

            }

        },

        resolveMaxWeeks: function() {

            for (let i = this.periods.length-1; i >= 0; i--) {

                const thisPeriod = this.periods[i]
                const nextPeriodsDuration = this.periods.slice(i+1).reduce( (sum, p) => sum + this.periodDuration(p), 0)

                if ( thisPeriod.start != null ) {

                    const week = thisPeriod.start.clone()
                            .add( this.periodDuration(thisPeriod), 'weeks' )
                            .diff( this.start, 'weeks')

                    const weeksInPast = this.relativeToPeriods ? this.firstPeriod.start.diff( this.start, 'weeks') : this.weeksInPast
                    const overEnd = week + nextPeriodsDuration - weeksInPast - this.maxWeeksInFuture

                    if ( overEnd > 0 ) {
                        thisPeriod.start = thisPeriod.start.clone().subtract(overEnd, 'weeks')
                    }

                }

            }

        },


        /** Styling **/

        styleForPeriod: function(period) {
            return {
                transform: 'translateY(' + Math.floor(period.start.diff(this.start, 'weeks')) * 45 + 'px' + ')',
            }
        },

        styleForBlock: function(block) {
            return {
                height: block.duration * 45 + 'px',
            }
        },


    },

    created: function() {

        this.recomputeWeeks()
        this.autoResolveOverlap()

        interact('.planner__period')
            .draggable({
                autoScroll: true,
                snap: {
                    targets: [ interact.createSnapGrid({ x: 0, y: 45, range: Infinity, }) ],
                    relativePoints: [ { x: 0, y: 0 }, ],
                    offset: { x: 0,  y: 0 },
                },
                restrict: { restriction: 'parent', },
                onmove: this.onPeriodMove,
                axis: 'y',
            })
            .allowFrom('.planner__period-title')

        interact('.planner__block')
            .draggable({
                autoScroll: true,
                snap: {
                    targets: [ interact.createSnapGrid({ x: 0, y: 45, range: Infinity, }) ],
                    relativePoints: [ { x: 0, y: 0 }, ],
                    offset: { x: 0,  y: 0 },
                },
                restrict: { restriction: '.periods', },
                onmove: this.onBlockMove,
                axis: 'y',
            })


    }

}
</script>


<style lang="scss">

    @import 'global';


    .planner {

        display: flex;
        flex-direction: row;
        box-sizing: border-box;

        &__calendar {
            display: flex;
            flex-direction: column;
        }

        &__header {
            display: flex;
            flex-direction: row;
        }

        &__week {
            display: flex;
            flex-direction: row;
        }

        &__week-days-wrapper {
            display: flex;
            flex-direction: row;

        }

        &__week-relative-number {
            font-size: 0.8em;
            text-align: center;
            width: $planner__column-width;
            line-height: $planner__row-height;
        }

        &__week-number {
            font-size: 0.8em;
            font-weight: $font-body-bold-weight;
            width: 2*$planner__column-width;
            text-align: left;
            padding-left: 10px;
            line-height: $planner__row-height;
        }

        &__week-date {
            font-size: 0.8em;
            line-height: $planner__row-height;
            padding-left: 5px;
        }

        &__day {
            width: $planner__column-width;
            height: $planner__row-height;
            line-height: $planner__row-height;
            border-left: 1px solid #f3f3f3;
            border-bottom: 1px solid #f3f3f3;

            &--saturday, &--sunday {
                background-color: rgba(0,0,0,0.02);
            }

            &--past {
                background-color: rgba(0,0,0,0.1);
            }

            &--current {
                background-color: #aff;
            }

            &:last-child {
                border-right: 1px solid #eee;
            }

            &--heading {
                font-size: 0.8em;
                border-left: none;
                border-right: none;
                font-weight: $font-body-bold-weight;
                text-align: center;

                &:last-child {
                    border-right: none;
                }
            }

        }


        &__periods-wrapper {
            -moz-user-select: none;
            -webkit-user-select: none;
            -ms-user-select: none;
            user-select: none;

            width: 6em;
            position: relative;
            margin-top: $planner__row-height;
        }

        &__period {
            touch-action: none;
            -webkit-tap-highlight-color: rgba(0,0,0,0);

            position: absolute;
            left: 0;
            width: 6em;
            background-color: rgba(0, 0, 0, 0.1);

            &:before, &:after {
                content: '';
                display: block;
                width: 308px;
                border-top: 1px solid #000;
                position: absolute;
                z-index: 100;
            }

            &:before {
                top: 0;
            }

            &:after {
                bottom: 0;
            }

            &.is-selected {
                border: 1px solid #000;
                margin-top: -1px;
            }

            &.is-finalized.is-disabled {
                display: none;
            }

        }

        &__period-title {
            position: absolute;
            z-index: 100;
            cursor: move;
            width: 80%;
            padding: 5px 10px;
        }

        &__enabler {
            position: absolute;
            bottom: 0;
            z-index: 100;
        }

        &__block {
            position: relative;
            width: 100%;
            border-bottom: 1px solid #fff;

            &.is-disabled {
                display: none;
            }
        }

        &__extend-handle {
            position: absolute;
            z-index: 100;
            cursor: ns-resize;
            right: 5px;
            bottom: 5px;
        }

    }


</style>



// WEBPACK FOOTER //
// WeekPlanner.vue?58885ebc